import { Button } from "@secure-vault/ui";
import {
  ArrowRight,
  FileKey2,
  Globe2,
  KeyRound,
  Lock,
  Palette,
  ShieldCheck,
  Users,
} from "lucide-react";

import { Footer } from "@/features/landing/components/footer";
import { Pricing } from "@/features/landing/components/pricing";
import {
  BrandedLoginMockup,
  VaultDashboardMockup,
} from "@/features/landing/components/product-mockups";
import { appUrl, vaultAppUrl } from "@/lib/app-links";

const features = [
  {
    icon: Lock,
    title: "Encrypted Before Upload",
    description:
      "Each file is encrypted on your client's device before it is uploaded. Secure Legacy cannot read the contents, helping keep sensitive information protected even if storage systems are compromised.",
  },
  {
    icon: Palette,
    title: "Your Brand, Front and Centre",
    description:
      "Every client and trustee touchpoint carries your practice name, logo, colours and dedicated portal address. Secure Legacy works quietly behind the scenes.",
  },
  {
    icon: KeyRound,
    title: "Passwords We Never Store",
    description:
      "Client master passwords never leave their device and are never stored by Secure Legacy. A breach of our systems cannot reveal a password we do not hold.",
  },
  {
    icon: Users,
    title: "Trustee Protection by Design",
    description:
      "Clients nominate trusted family members or professionals. Encrypted key fragments are securely held for them, so no single trustee can unlock the vault alone.",
  },
  {
    icon: FileKey2,
    title: "One Secure Place for What Matters",
    description:
      "Clients can organise wills, insurance policies, trust deeds, password records and personal letters in one protected vault. Every file is secured with AES-256 encryption.",
  },
  {
    icon: ShieldCheck,
    title: "You Control the Handover",
    description:
      "Your client remains in control of their vault during their lifetime. Access for nominated trustees begins only when you initiate the secure release.",
  },
];

export default function LandingPage() {
  return (
    <div className="bg-background text-foreground min-h-screen font-sans antialiased">
      {/* Nav */}
      <header className="border-border bg-card border-b">
        <div className="container mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <Lock className="text-primary h-6 w-6" />
            <span className="font-display text-foreground text-lg font-bold tracking-tight">
              Secure Legacy
            </span>
          </div>
          <nav className="flex items-center gap-6">
            <a
              href="#features"
              className="text-muted-foreground hover:text-primary text-sm transition-colors"
            >
              Features
            </a>
            <a
              href="#pricing"
              className="text-muted-foreground hover:text-primary text-sm transition-colors"
            >
              Pricing
            </a>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" asChild>
                <a href={appUrl("/signin")}>Sign in</a>
              </Button>
              <Button size="sm" asChild>
                <a href={appUrl("/register")}>
                  Get Started
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </a>
              </Button>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="border-border border-b bg-[var(--ds-color-secondary-50)]/50 py-24">
        <div className="container mx-auto max-w-6xl px-6 text-center">
          <h1 className="font-display text-foreground text-5xl font-bold tracking-tight sm:text-6xl">
            Protect their legacy.
            <br />
            <span className="text-[var(--ds-color-secondary-500)]">
              Stay central when it matters most.
            </span>
          </h1>
          <p className="text-muted-foreground mx-auto mt-6 max-w-2xl text-lg">
            Secure Legacy is a secure digital vault for estate planning,
            delivered entirely under your practice&apos;s brand. Help clients
            protect their most important documents and instructions, while you
            remain the trusted professional who initiates access for nominated
            trustees when the time comes.
          </p>
          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Button
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-primary-hover px-8 py-6 text-base shadow-[var(--ds-shadow-brand)] transition-all duration-200 hover:-translate-y-[1px]"
              asChild
            >
              <a href={appUrl("/register")}>
                Start for Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="px-8 py-6 text-base"
              asChild
            >
              <a href={vaultAppUrl("/")}>Vault owner? Sign in</a>
            </Button>
          </div>
          <div className="mt-4 flex justify-center">
            <a
              href="#features"
              className="text-muted-foreground hover:text-primary text-sm font-medium underline-offset-4 transition-colors hover:underline"
            >
              See how it works
            </a>
          </div>

          <div className="mt-14 text-left">
            <VaultDashboardMockup />
            <p className="text-muted-foreground mt-4 text-center text-xs">
              Illustrative example of a fully branded client vault.
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-background py-20">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="mb-14 text-center">
            <h2 className="font-display text-foreground text-3xl font-bold tracking-tight sm:text-4xl">
              Stronger protection. A more valuable relationship.
            </h2>
            <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-lg">
              Protect sensitive client information without asking clients to
              send passwords, wills, trust deeds or private instructions by
              email. Secure Legacy gives every client a simple, encrypted vault
              presented under your practice&apos;s brand&mdash;and gives your
              team a clear role in the eventual handover.
            </p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div
                key={f.title}
                className="border-border bg-card rounded-[var(--ds-radius-lg)] border p-6 shadow-[var(--ds-shadow-md)] transition-shadow duration-200 hover:shadow-[var(--ds-shadow-lg)]"
              >
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-[var(--ds-radius-md)] bg-[var(--ds-color-secondary-50)]">
                  <f.icon className="h-5 w-5 text-[var(--ds-color-secondary-500)]" />
                </div>
                <h3 className="font-display text-foreground text-base font-semibold">
                  {f.title}
                </h3>
                <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                  {f.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Branded experience */}
      <section
        id="branding"
        className="bg-[var(--ds-color-secondary-500)] py-20 text-white"
      >
        <div className="container mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <p className="text-primary font-display text-xs font-semibold tracking-wider uppercase">
              A seamless extension of your practice
            </p>
            <h2 className="font-display mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Your brand, from first invitation to final handover.
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-zinc-200">
              Secure Legacy operates behind the scenes. Clients and trustees use
              a dedicated portal featuring your practice name, logo, colours and
              domain&mdash;creating a consistent, trusted experience at every
              step.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
              {[
                {
                  icon: Palette,
                  title: "Your visual identity",
                  body: "Your logo, colours and practice name throughout.",
                },
                {
                  icon: Globe2,
                  title: "Your dedicated portal",
                  body: "A branded address your clients recognise and trust.",
                },
                {
                  icon: Users,
                  title: "One consistent journey",
                  body: "Your brand stays present for clients and trustees.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex items-start gap-4 rounded-[var(--ds-radius-lg)] border border-white/15 bg-white/10 p-5"
                >
                  <item.icon className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                  <div>
                    <h3 className="font-display font-semibold text-white">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-sm text-zinc-300">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:pl-4">
            <BrandedLoginMockup />
            <p className="mt-4 text-center text-xs text-zinc-400">
              Illustrative Apex Financial client sign-in.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section
        id="how-it-works"
        className="border-border bg-muted/20 border-y py-20"
      >
        <div className="container mx-auto max-w-4xl px-6">
          <h2 className="font-display text-foreground mb-12 text-center text-3xl font-bold tracking-tight">
            How it works
          </h2>
          <ol className="border-border relative ml-4 space-y-10 border-l">
            {[
              {
                step: "1",
                title: "You create their branded vault",
                body: "You set up a secure vault featuring your practice name, logo and colours, then invite your client. To them, it feels like a natural extension of your service.",
              },
              {
                step: "2",
                title: "Your client adds what matters",
                body: "They upload important documents, records and personal instructions. File contents are encrypted in their browser before they ever leave their device.",
              },
              {
                step: "3",
                title: "They choose their trustees",
                body: "Your client nominates trusted family members or professionals. The platform securely holds encrypted key fragments until they are needed, and no single trustee can access the vault alone.",
              },
              {
                step: "4",
                title: "You begin the secure handover",
                body: "Following a triggering event and your usual verification process, you initiate the release. Trustees receive secure access through your branded portal and work together to decrypt the vault in their browser.",
              },
            ].map((item) => (
              <li key={item.step} className="ml-8">
                <span className="border-border bg-card absolute -left-4 flex h-8 w-8 items-center justify-center rounded-full border text-sm font-bold text-[var(--ds-color-secondary-500)]">
                  {item.step}
                </span>
                <h3 className="font-display text-foreground text-base font-semibold">
                  {item.title}
                </h3>
                <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">
                  {item.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Pricing */}
      <Pricing />

      {/* Footer */}
      <Footer />
    </div>
  );
}
